import React from 'react'
import { Button, Heading, Text, LoadingSpinner } from '../../components/ui'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { useAuthContext } from '../../contexts/AuthContext'
import { supportCenterSpecialtiesService } from '../../services/supportCenterSpecialties'
import type {
  SupportCenterProfileOption,
  SupportCenterSpecializationAssignment,
  SupportCenterUserUnavailability,
} from '../../types/supportCenterSpecialties'
import { ArrowPathIcon, PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { buildIsoDate, formatDateBr, parseIsoDate } from '../../components/centralAmparo/scheduleHelpers'
import {
  MODULE_NAME,
  clampDay,
  defaultUnavailabilityPeriodRange,
  getErrorMessage,
  periodsOverlap,
  todayParts,
  type IndisponibilidadeSubTab,
} from '../../components/centralAmparo/parametrosHelpers'
import ParametrosUnavailModal, {
  type UnavailModalState,
} from './ParametrosUnavailModal'
import ParametrosDeleteModal from './ParametrosDeleteModal'
import ParametrosFeriadosTab from './ParametrosFeriadosTab'

const filterInputClass =
  'rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500'

const ParametrosIndisponibilidadeTab: React.FC = () => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete } = useUserLogs()
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')

  const [subTab, setSubTab] = React.useState<IndisponibilidadeSubTab>('por_profissional')
  const [loading, setLoading] = React.useState(true)
  const [pageError, setPageError] = React.useState<string | null>(null)
  const [assignments, setAssignments] = React.useState<SupportCenterSpecializationAssignment[]>(
    []
  )
  const [unavailability, setUnavailability] = React.useState<SupportCenterUserUnavailability[]>(
    []
  )
  const [professionalFilter, setProfessionalFilter] = React.useState('')
  const defaultRange = React.useMemo(() => defaultUnavailabilityPeriodRange(), [])
  const [periodFrom, setPeriodFrom] = React.useState(defaultRange.from)
  const [periodTo, setPeriodTo] = React.useState(defaultRange.to)

  const [unavailModal, setUnavailModal] = React.useState<UnavailModalState | null>(null)
  const [submittingUnavail, setSubmittingUnavail] = React.useState(false)
  const [unavailError, setUnavailError] = React.useState<string | null>(null)

  const [deleteModal, setDeleteModal] = React.useState<{ id: string; label: string } | null>(null)
  const [deleteReason, setDeleteReason] = React.useState('')
  const [submittingDelete, setSubmittingDelete] = React.useState(false)

  const loadData = React.useCallback(async () => {
    setLoading(true)
    setPageError(null)
    try {
      const [assigns, unavails] = await Promise.all([
        supportCenterSpecialtiesService.listAssignments(),
        supportCenterSpecialtiesService.listUnavailability(),
      ])
      setAssignments(assigns)
      setUnavailability(unavails)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    if (subTab !== 'por_profissional') return
    void loadData()
  }, [loadData, subTab])

  const assignedProfileOptions = React.useMemo(() => {
    const byId = new Map<string, SupportCenterProfileOption>()
    for (const a of assignments) {
      if (a.profile?.id) {
        byId.set(a.profile.id, { id: a.profile.id, name: a.profile.name })
      }
    }
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  }, [assignments])

  const filteredUnavailability = React.useMemo(
    () =>
      unavailability
        .filter((u) => {
          if (professionalFilter && u.user_id !== professionalFilter) return false
          return periodsOverlap(u.starts_on, u.ends_on, periodFrom, periodTo)
        })
        .sort((a, b) => {
          const byStart = a.starts_on.localeCompare(b.starts_on)
          if (byStart !== 0) return byStart
          const byEnd = a.ends_on.localeCompare(b.ends_on)
          if (byEnd !== 0) return byEnd
          return (a.profile?.name ?? '').localeCompare(b.profile?.name ?? '', 'pt-BR')
        }),
    [unavailability, professionalFilter, periodFrom, periodTo]
  )

  const restorePeriod = () => {
    const next = defaultUnavailabilityPeriodRange()
    setPeriodFrom(next.from)
    setPeriodTo(next.to)
  }

  const subTabClass = (tab: IndisponibilidadeSubTab) =>
    `whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
      subTab === tab
        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
    }`

  const openNewUnavail = () => {
    const start = todayParts()
    setUnavailError(null)
    setUnavailModal({
      user_id: professionalFilter || '',
      start,
      end: { ...start },
      spanDays: false,
      all_day: true,
      hours: [],
      reason: '',
    })
  }

  const openEditUnavail = (item: SupportCenterUserUnavailability) => {
    const start = parseIsoDate(item.starts_on)
    const end = parseIsoDate(item.ends_on)
    setUnavailError(null)
    setUnavailModal({
      id: item.id,
      user_id: item.user_id,
      start,
      end,
      spanDays: item.starts_on.slice(0, 10) !== item.ends_on.slice(0, 10),
      all_day: item.all_day,
      hours: item.hours ?? [],
      reason: item.reason ?? '',
    })
  }

  const closeUnavailModal = () => {
    if (submittingUnavail) return
    setUnavailModal(null)
    setUnavailError(null)
  }

  const handleUnavailSave = async () => {
    if (!unavailModal || !user?.id) return
    if (!unavailModal.user_id) {
      setUnavailError('Selecione o profissional.')
      return
    }

    const start = clampDay(unavailModal.start)
    const end = unavailModal.spanDays
      ? clampDay(unavailModal.end)
      : { ...start }
    const startsOn = buildIsoDate(start.year, start.month, start.day)
    const endsOn = buildIsoDate(end.year, end.month, end.day)

    if (unavailModal.spanDays && endsOn < startsOn) {
      setUnavailError('A data final deve ser maior ou igual à data inicial.')
      return
    }
    if (!unavailModal.all_day && unavailModal.hours.length === 0) {
      setUnavailError('Selecione ao menos uma hora ou marque dia(s) inteiro(s).')
      return
    }

    setSubmittingUnavail(true)
    setUnavailError(null)
    try {
      const savedId = await supportCenterSpecialtiesService.upsertUnavailability({
        id: unavailModal.id,
        user_id: unavailModal.user_id,
        starts_on: startsOn,
        ends_on: endsOn,
        all_day: unavailModal.all_day,
        hours: unavailModal.hours,
        reason: unavailModal.reason,
        actor_id: user.id,
      })
      const profileName =
        assignedProfileOptions.find((p) => p.id === unavailModal.user_id)?.name ??
        unavailModal.user_id
      const periodLabel =
        startsOn === endsOn
          ? formatDateBr(startsOn)
          : `${formatDateBr(startsOn)} a ${formatDateBr(endsOn)}`
      void (unavailModal.id
        ? logUpdate(
            'support_center_user_unavailability',
            unavailModal.id,
            `Atualizou indisponibilidade: ${profileName} (${periodLabel})`
          )
        : logCreate(
            'support_center_user_unavailability',
            savedId,
            `Criou indisponibilidade: ${profileName} (${periodLabel})`
          ))
      closeUnavailModal()
      await loadData()
    } catch (error) {
      setUnavailError(getErrorMessage(error))
    } finally {
      setSubmittingUnavail(false)
    }
  }

  const closeDeleteModal = () => {
    if (submittingDelete) return
    setDeleteModal(null)
    setDeleteReason('')
  }

  const confirmDelete = async () => {
    if (!deleteModal || !user?.id || !deleteReason.trim()) return
    setSubmittingDelete(true)
    try {
      await supportCenterSpecialtiesService.softDeleteUnavailability(
        deleteModal.id,
        deleteReason.trim(),
        user.id
      )
      void logDelete(
        'support_center_user_unavailability',
        deleteModal.id,
        `Excluiu indisponibilidade: ${deleteModal.label}`,
        {
          reason: deleteReason.trim(),
        }
      )
      closeDeleteModal()
      await loadData()
    } catch (error) {
      setPageError(getErrorMessage(error))
      closeDeleteModal()
    } finally {
      setSubmittingDelete(false)
    }
  }

  return (
    <div className="p-6 space-y-4">
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-6 overflow-x-auto">
          <button
            type="button"
            onClick={() => setSubTab('por_profissional')}
            className={subTabClass('por_profissional')}
          >
            Por profissional
          </button>
          <button
            type="button"
            onClick={() => setSubTab('feriados')}
            className={subTabClass('feriados')}
          >
            Feriados
          </button>
        </nav>
      </div>

      {subTab === 'feriados' ? (
        <ParametrosFeriadosTab />
      ) : loading ? (
        <div className="flex-1 flex items-center justify-center p-6">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {pageError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
              <Text size="sm">{pageError}</Text>
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white flex-shrink-0">
                  Por profissional
                </Heading>
                <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
                  {filteredUnavailability.length}
                  {filteredUnavailability.length !== unavailability.length
                    ? ` de ${unavailability.length}`
                    : ''}{' '}
                  {filteredUnavailability.length === 1 ? 'registro' : 'registros'}
                </span>
              </div>
              <div className="flex items-center gap-3 flex-wrap flex-shrink-0">
                <select
                  value={professionalFilter}
                  onChange={(e) => setProfessionalFilter(e.target.value)}
                  className={`${filterInputClass} min-w-[12rem]`}
                  aria-label="Filtrar por profissional"
                >
                  <option value="">Todos os profissionais</option>
                  {assignedProfileOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <span className="shrink-0">De</span>
                  <input
                    type="date"
                    value={periodFrom}
                    onChange={(e) => setPeriodFrom(e.target.value)}
                    className={filterInputClass}
                    aria-label="Período de"
                  />
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <span className="shrink-0">Até</span>
                  <input
                    type="date"
                    value={periodTo}
                    onChange={(e) => setPeriodTo(e.target.value)}
                    className={filterInputClass}
                    aria-label="Período até"
                  />
                </label>
                <Button
                  variant="outline"
                  size="md"
                  onClick={restorePeriod}
                  title="Restaurar período (hoje → +60 dias)"
                >
                  <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                  Restaurar período
                </Button>
                {canInsert && (
                  <Button
                    variant="solid"
                    size="md"
                    onClick={openNewUnavail}
                    disabled={assignedProfileOptions.length === 0}
                  >
                    <PlusIcon className="h-4 w-4" />
                    Nova Indisponibilidade
                  </Button>
                )}
              </div>
            </div>

            {filteredUnavailability.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <Text size="sm" color="muted">
                  {unavailability.length === 0
                    ? 'Nenhuma indisponibilidade cadastrada.'
                    : 'Nenhum registro para os filtros selecionados.'}
                </Text>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Profissional
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Período
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Motivo
                      </th>
                      {(canUpdate || canDelete) && (
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Ações
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredUnavailability.map((item) => {
                      const userName = item.profile?.name ?? '—'
                      const period = `${formatDateBr(item.starts_on)} – ${formatDateBr(item.ends_on)}`
                      const typeLabel = item.all_day
                        ? 'Dia(s) inteiro(s)'
                        : item.hours.map((h) => `${h.toString().padStart(2, '0')}h`).join(', ')
                      return (
                        <tr
                          key={item.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                            {userName}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {period}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                            {typeLabel}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                            {item.reason || '—'}
                          </td>
                          {(canUpdate || canDelete) && (
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-2">
                                {canUpdate && (
                                  <button
                                    type="button"
                                    title="Editar"
                                    onClick={() => openEditUnavail(item)}
                                    className="text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    type="button"
                                    title="Excluir"
                                    onClick={() => {
                                      setDeleteModal({
                                        id: item.id,
                                        label: `${userName} (${period})`,
                                      })
                                      setDeleteReason('')
                                    }}
                                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {unavailModal && (
            <ParametrosUnavailModal
              unavailModal={unavailModal}
              unavailError={unavailError}
              submitting={submittingUnavail}
              assignedProfileOptions={assignedProfileOptions}
              onChange={setUnavailModal}
              onClose={closeUnavailModal}
              onSave={() => void handleUnavailSave()}
            />
          )}

          {deleteModal && (
            <ParametrosDeleteModal
              label={deleteModal.label}
              deleteReason={deleteReason}
              submitting={submittingDelete}
              onReasonChange={setDeleteReason}
              onClose={closeDeleteModal}
              onConfirm={() => void confirmDelete()}
            />
          )}
        </>
      )}
    </div>
  )
}

export default ParametrosIndisponibilidadeTab
